import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Modal, Select, message, theme } from 'antd';
import dayjs from 'dayjs';
import { ChevronDown, Trash2, X } from 'lucide-react';
import { currencyFormatterToBRL } from '@/helpers/currencyParser';
import {
    getPropertyNamesFromProductStatus,
    ProductStatus,
} from '@/interfaces/Planogram';
import { useUpdateProductFields } from '@/store/Product/useUpdateProductFields';
import { QuickAdjustPlanogram as Planogram } from '../../types';
import {
    formatCents,
    getApiErrorMessage,
    getCalculatedMargin,
    getSuggestedAppPriceInCents,
    getTargetMargin,
} from '../../utils';
import { useSavePlanogramCatalog } from '../../useSavePlanogramCatalog';
import {
    getQuickAdjustModalLayout,
    useIsFullScreenModal,
} from '../modalLayout';
import { NumberStepper, PriceStepper } from '../Stepper';
import {
    Body,
    SELECT_POPUP_CLASS,
    SelectPopupGlobalStyle,
} from '../AddProductModal/styles';
import * as S from './styles';

const { Option } = Select;

type EditProductModalProps = {
    open: boolean;
    planogram: Planogram | undefined;
    onClose: () => void;
    onSaved: () => void;
};

export const EditProductModal = ({
    open,
    planogram,
    onClose,
    onSaved,
}: EditProductModalProps) => {
    const [stockAmount, setStockAmount] = useState(0);
    const [alertLevel, setAlertLevel] = useState(0);
    const [standardAmount, setStandardAmount] = useState(0);
    const [dueDate, setDueDate] = useState<string | null>(null);
    const [status, setStatus] = useState('unlocked');
    const [planogramCode, setPlanogramCode] = useState('');
    const [priceListInCents, setPriceListInCents] = useState(0);
    const [priceFinalInCents, setPriceFinalInCents] = useState(0);
    const [lastPriceInCents, setLastPriceInCents] = useState(0);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const isFullScreen = useIsFullScreenModal();
    const { token } = theme.useToken();

    const { mutateAsync: savePlanogramCatalog, isLoading: isSaving } =
        useSavePlanogramCatalog();
    const { mutateAsync: updateProductFields } = useUpdateProductFields();

    useEffect(() => {
        if (!planogram) return;
        setStockAmount(planogram.stock_amount);
        setAlertLevel(planogram.alert_level);
        setStandardAmount(planogram.standard_amount);
        setDueDate(
            planogram.due_date
                ? dayjs(planogram.due_date).format('YYYY-MM-DD')
                : null,
        );
        setStatus(planogram.status);
        setPlanogramCode(planogram.planogram_code);
        setPriceListInCents(planogram.price_list_in_cents);
        setPriceFinalInCents(planogram.price_final_in_cents);
        setLastPriceInCents(planogram.product.last_price_in_cents ?? 0);
        setDeleteConfirmOpen(false);
    }, [planogram]);

    const targetMargin = useMemo(
        () => (planogram ? getTargetMargin(planogram) : null),
        [planogram],
    );

    const suggestedAppPriceInCents = useMemo(
        () => getSuggestedAppPriceInCents(lastPriceInCents, targetMargin),
        [lastPriceInCents, targetMargin],
    );

    const calculatedMargin = useMemo(
        () => getCalculatedMargin(priceFinalInCents, lastPriceInCents),
        [priceFinalInCents, lastPriceInCents],
    );

    const diffVsSuggested =
        suggestedAppPriceInCents != null
            ? priceFinalInCents - suggestedAppPriceInCents
            : null;

    if (!planogram) return null;

    const handleUseSuggested = () => {
        if (suggestedAppPriceInCents != null) {
            setPriceFinalInCents(suggestedAppPriceInCents);
        }
    };

    const handleSave = async () => {
        try {
            await savePlanogramCatalog({
                storeId: planogram.store_id,
                dataUpdate: [
                    {
                        id: planogram.id,
                        product_id: planogram.product_id,
                        planogram_code: planogramCode,
                        bar_code: planogram.product.BarCodes?.[0]?.code ?? '',
                        product_name: planogram.product.name,
                        standard_amount: standardAmount,
                        price_list_in_cents: priceListInCents,
                        price_final_in_cents: priceFinalInCents,
                        status,
                        alert_level: alertLevel,
                        due_date: dueDate,
                        stock_amount: stockAmount,
                    },
                ],
            });
        } catch (error) {
            message.error(
                getApiErrorMessage(
                    error,
                    'Não foi possível salvar as alterações. Tente novamente.',
                ),
            );
            return;
        }

        if (lastPriceInCents !== (planogram.product.last_price_in_cents ?? 0)) {
            try {
                await updateProductFields({
                    productId: planogram.product.id,
                    overrides: { lastPriceInCents },
                });
            } catch (error) {
                message.error(
                    getApiErrorMessage(
                        error,
                        'Planograma salvo, mas não foi possível atualizar o último preço de compra do produto.',
                    ),
                );
                onSaved();
                return;
            }
        }

        message.success('Alterações salvas');
        onSaved();
        onClose();
    };

    const handleDelete = async () => {
        try {
            await savePlanogramCatalog({
                storeId: planogram.store_id,
                dataDelete: [planogram.id],
            });
            message.success('Produto removido do planograma');
            onSaved();
            onClose();
        } catch (error) {
            message.error(
                getApiErrorMessage(
                    error,
                    'Não foi possível excluir o produto. Tente novamente.',
                ),
            );
        }
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div>Editar produto</div>
                        <div style={{ fontSize: 12, fontWeight: 400 }}>
                            {planogram.product.name}
                        </div>
                    </div>
                    <S.CloseButton type="button" onClick={onClose}>
                        <X size={18} />
                    </S.CloseButton>
                </div>
            }
            open={open}
            onCancel={onClose}
            closable={false}
            {...getQuickAdjustModalLayout(isFullScreen, token)}
            footer={
                <S.FooterActions>
                    <S.CancelButton key="cancel" onClick={onClose}>
                        Cancelar
                    </S.CancelButton>
                    <S.SaveButton
                        key="save"
                        type="primary"
                        loading={isSaving}
                        onClick={handleSave}
                    >
                        Salvar alterações
                    </S.SaveButton>
                </S.FooterActions>
            }
            destroyOnHidden
        >
            <SelectPopupGlobalStyle />
            <Body>
                <S.Section>
                    <S.SectionTitle>Estoque</S.SectionTitle>
                    <S.FieldsGrid>
                        <div>
                            <S.FieldLabel>
                                Quantidade em estoque (atual)
                            </S.FieldLabel>
                            <NumberStepper
                                value={stockAmount}
                                onChange={setStockAmount}
                            />
                        </div>
                        <div>
                            <S.FieldLabel>Nível de alerta</S.FieldLabel>
                            <NumberStepper
                                value={alertLevel}
                                onChange={setAlertLevel}
                            />
                        </div>
                        <div>
                            <S.FieldLabel>Quantidade padrão</S.FieldLabel>
                            <NumberStepper
                                value={standardAmount}
                                onChange={setStandardAmount}
                            />
                        </div>
                    </S.FieldsGrid>
                </S.Section>

                <S.Section>
                    <S.SectionTitle>Operacional</S.SectionTitle>
                    <S.FieldsGrid>
                        <div>
                            <S.FieldLabel>Data de vencimento</S.FieldLabel>
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                value={dueDate ? dayjs(dueDate) : null}
                                onChange={(value) =>
                                    setDueDate(
                                        value
                                            ? value.format('YYYY-MM-DD')
                                            : null,
                                    )
                                }
                            />
                        </div>
                        <div>
                            <S.FieldLabel>Status</S.FieldLabel>
                            <Select
                                style={{ width: '100%' }}
                                value={status}
                                onChange={setStatus}
                                classNames={{
                                    popup: { root: SELECT_POPUP_CLASS },
                                }}
                                suffixIcon={
                                    <ChevronDown
                                        size={16}
                                        color={token.colorTextTertiary}
                                    />
                                }
                            >
                                {getPropertyNamesFromProductStatus().map(
                                    (key) => (
                                        <Option key={key} value={key}>
                                            {
                                                ProductStatus[
                                                    key as keyof typeof ProductStatus
                                                ]
                                            }
                                        </Option>
                                    ),
                                )}
                            </Select>
                        </div>
                    </S.FieldsGrid>
                </S.Section>

                <S.Section>
                    <S.SectionTitle>Preços</S.SectionTitle>
                    <S.FieldsGrid>
                        <div>
                            <S.FieldLabel>Preço lista</S.FieldLabel>
                            <PriceStepper
                                value={priceListInCents}
                                onChange={setPriceListInCents}
                            />
                        </div>
                        <div>
                            <S.FieldLabel>Preço App</S.FieldLabel>
                            <PriceStepper
                                value={priceFinalInCents}
                                onChange={setPriceFinalInCents}
                                accent
                            />
                        </div>

                        <S.SuggestedBox>
                            <div>
                                <span style={{ fontSize: 12 }}>
                                    Preço App sugerido
                                </span>
                                {suggestedAppPriceInCents != null ? (
                                    <S.SuggestedValue>
                                        {currencyFormatterToBRL(
                                            suggestedAppPriceInCents,
                                        )}
                                    </S.SuggestedValue>
                                ) : (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: token.colorTextTertiary,
                                        }}
                                    >
                                        Sem sugestão — margem do
                                        produto/categoria não cadastrada.
                                    </div>
                                )}
                            </div>
                            {suggestedAppPriceInCents != null && (
                                <S.UseSuggestedButton
                                    type="text"
                                    onClick={handleUseSuggested}
                                >
                                    Usar sugerido
                                </S.UseSuggestedButton>
                            )}
                        </S.SuggestedBox>

                        <div>
                            <S.FieldLabel>
                                Último preço de compra un.
                            </S.FieldLabel>
                            <PriceStepper
                                value={lastPriceInCents}
                                onChange={setLastPriceInCents}
                            />
                        </div>
                    </S.FieldsGrid>
                </S.Section>

                <S.Section>
                    <S.SectionTitle>Impacto da alteração</S.SectionTitle>
                    <S.ImpactBox>
                        <S.ImpactItem>
                            Margem calculada
                            <S.ImpactValue>
                                {calculatedMargin != null
                                    ? `${Math.round(calculatedMargin * 100)}%`
                                    : '—'}
                            </S.ImpactValue>
                        </S.ImpactItem>
                        <S.ImpactItem>
                            Margem do produto/categoria
                            <S.ImpactValue
                                color={
                                    targetMargin == null
                                        ? token.colorTextTertiary
                                        : undefined
                                }
                            >
                                {targetMargin != null
                                    ? `${Math.round(targetMargin * 100)}%`
                                    : 'Não cadastrada'}
                            </S.ImpactValue>
                        </S.ImpactItem>
                        <S.ImpactItem>
                            Diferença vs. sugerido
                            <S.ImpactValue
                                color={
                                    diffVsSuggested == null
                                        ? token.colorTextTertiary
                                        : diffVsSuggested < 0
                                        ? token.colorError
                                        : undefined
                                }
                            >
                                {diffVsSuggested != null
                                    ? `${
                                          diffVsSuggested < 0 ? '-' : '+'
                                      }${formatCents(
                                          Math.abs(diffVsSuggested),
                                      )}`
                                    : '—'}
                            </S.ImpactValue>
                        </S.ImpactItem>
                    </S.ImpactBox>
                </S.Section>

                <S.Section>
                    <S.FieldLabel>Código de planograma</S.FieldLabel>
                    <input
                        value={planogramCode}
                        onChange={(e) => setPlanogramCode(e.target.value)}
                        style={{
                            width: '100%',
                            height: 42,
                            borderRadius: 10,
                            border: `1px solid ${token.colorBorderSecondary}`,
                            background: token.colorBgContainer,
                            color: token.colorText,
                            padding: '0 11px',
                            boxSizing: 'border-box',
                            fontFamily: "'SFMono-Regular', Consolas, monospace",
                        }}
                    />
                </S.Section>

                <S.DeleteSection>
                    {deleteConfirmOpen ? (
                        <S.DeleteConfirmBox>
                            <S.DeleteConfirmText>
                                Excluir este produto do planograma? Essa ação
                                não pode ser desfeita.
                            </S.DeleteConfirmText>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <Button
                                    style={{ flex: 1 }}
                                    onClick={() => setDeleteConfirmOpen(false)}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    danger
                                    type="primary"
                                    style={{ flex: 1 }}
                                    onClick={handleDelete}
                                >
                                    Confirmar exclusão
                                </Button>
                            </div>
                        </S.DeleteConfirmBox>
                    ) : (
                        <S.DeleteButton
                            type="text"
                            danger
                            icon={<Trash2 size={15} />}
                            onClick={() => setDeleteConfirmOpen(true)}
                        >
                            Excluir produto do planograma
                        </S.DeleteButton>
                    )}
                </S.DeleteSection>
            </Body>
        </Modal>
    );
};

export default EditProductModal;
